import { useState } from "react";
import Checkbox from "../../components/Checkbox";
import Input from "../../components/Input";
import Switch from "../../components/Switch";
import { formatAmount } from "../../utils/currency";
import { RatePlan, RatePlanType } from "./RatePlans";
import { AccountHolderRatePlan } from "../../services/accountService";
import { useRatePlansContext } from "../../hooks/data/providers/ratePlansContext";
import Tooltip from "../../components/Tooltip";

type RatePlanRowProps = {
  ratePlan: RatePlan;
  ratePlanType: RatePlanType;
  index: number;
  isEditing: boolean;
  addToInputRefs: (e: HTMLInputElement) => void;
  isMasterSurchargeEnabled: boolean;
  rowDisabled?: boolean;
};

function RatePlanRow({
  ratePlan,
  ratePlanType,
  isEditing,
  addToInputRefs,
  isMasterSurchargeEnabled,
  rowDisabled,
}: RatePlanRowProps) {
  const [surchargeIsChecked, setSurchargeIsChecked] = useState(false);
  const { modify: modifyRatePlansCtx } = useRatePlansContext();

  function handleCurrentEditingRatePlanChange(
    updatedData: Partial<AccountHolderRatePlan>
  ) {
    // Update the ratePlans in context
    const surchargeValue =
      ratePlan.data?.surcharge == null ? false : ratePlan.data.surcharge;

    modifyRatePlansCtx(
      { ...ratePlan },
      {
        surcharge: surchargeValue,
        ...updatedData, // updatedData overrides default if it provides surcharge
      }
    );
  }

  return (
    <div
      className={`${
        ratePlanType === "Flat Rate Plan" ? "grid-cols-8" : "grid-cols-7"
      } grid items-center gap-x-3`}
    >
      <span className="font-medium capitalize">{ratePlan.label}</span>
      <div className="px-6">
        {ratePlan.data?.paymentMethodType == "interac_card" &&
        rowDisabled &&
        isEditing ? (
          <Tooltip
            text="Google and/or Apple Pay must be enabled in order to enable Interac."
            position="top"
          >
            <Checkbox
              key={ratePlan.data?.paymentMethodType}
              checked={ratePlan.data?.allowedMethod}
              onChange={(e) => {
                handleCurrentEditingRatePlanChange({
                  allowedMethod: e.target.checked,
                });
              }}
              disabled={!isEditing || rowDisabled}
            />
          </Tooltip>
        ) : (
          <Checkbox
            key={ratePlan.data?.paymentMethodType}
            checked={ratePlan.data?.allowedMethod}
            onChange={(e) => {
              handleCurrentEditingRatePlanChange({
                allowedMethod: e.target.checked,
              });
            }}
            disabled={!isEditing || rowDisabled}
          />
        )}
      </div>
      {/* Online Percentage/Online Markup */}
      <Input
        id="creditCardRatePlanPercentage"
        type="number"
        ref={addToInputRefs}
        value={
          isEditing
            ? ratePlanType === "Flat Rate Plan"
              ? ratePlan?.data?.percentageValue
              : ratePlan?.data?.icPercentage
            : formatAmount(
                ratePlanType === "Flat Rate Plan"
                  ? ratePlan.data?.percentageValue
                  : ratePlan.data?.icPercentage
              )
        }
        onChange={(e) =>
          handleCurrentEditingRatePlanChange(
            ratePlanType === "Flat Rate Plan"
              ? {
                  percentageValue: e.target.value,
                }
              : {
                  icPercentage: e.target.value,
                }
          )
        }
        disabled={!isEditing || rowDisabled}
      />
      {/* Online Transaction */}
      <Input
        id="creditCardRatePlanFixedAmount"
        type="number"
        value={
          isEditing
            ? ratePlanType === "Flat Rate Plan"
              ? ratePlan.data?.fixedAmount
              : ratePlan.data?.icFixedAmount
            : formatAmount(
                ratePlanType === "Flat Rate Plan"
                  ? ratePlan.data?.fixedAmount
                  : ratePlan.data?.icFixedAmount
              )
        }
        onChange={(e) =>
          handleCurrentEditingRatePlanChange(
            ratePlanType === "Flat Rate Plan"
              ? {
                  fixedAmount: e.target.value,
                }
              : {
                  icFixedAmount: e.target.value,
                }
          )
        }
        disabled={!isEditing || rowDisabled}
      />
      {/* In Person Percentage/In Person Markup */}
      <Input
        id="creditCardRatePlanIppPercentage"
        type="number"
        ref={addToInputRefs}
        value={
          isEditing
            ? ratePlanType === "Flat Rate Plan"
              ? ratePlan.data?.ippPercentage
              : ratePlan.data?.icIppPercentage
            : formatAmount(
                ratePlanType === "Flat Rate Plan"
                  ? ratePlan.data?.ippPercentage
                  : ratePlan.data?.icIppPercentage
              )
        }
        onChange={(e) =>
          handleCurrentEditingRatePlanChange(
            ratePlanType === "Flat Rate Plan"
              ? {
                  ippPercentage: e.target.value,
                }
              : {
                  icIppPercentage: e.target.value,
                }
          )
        }
        disabled={!isEditing || rowDisabled}
      />
      {/* In Person Transaction */}
      <Input
        id="creditCardRatePlanIppFixedAmount"
        type="number"
        value={
          isEditing
            ? ratePlanType === "Flat Rate Plan"
              ? ratePlan.data?.ippFixedAmount
              : ratePlan.data?.icIppFixedAmount
            : formatAmount(
                ratePlanType === "Flat Rate Plan"
                  ? ratePlan.data?.ippFixedAmount
                  : ratePlan.data?.icIppFixedAmount
              )
        }
        onChange={(e) =>
          handleCurrentEditingRatePlanChange(
            ratePlanType === "Flat Rate Plan"
              ? {
                  ippFixedAmount: e.target.value,
                }
              : {
                  icIppFixedAmount: e.target.value,
                }
          )
        }
        disabled={!isEditing || rowDisabled}
      />
      {(ratePlanType === "Flat Rate Plan" ||
        process.env.REACT_APP_FF_DISPLAY_ICPP_SURCHARGE?.toLocaleLowerCase() ===
          "true") && (
        <>
          <Tooltip
            text="Only applied when master Surcharge Rate is enabled."
            position="right"
            hide={!isEditing || isMasterSurchargeEnabled}
          >
            <Switch
              checked={ratePlan.data?.surcharge ?? false}
              onChange={() => {
                setSurchargeIsChecked((p) => !p);
                handleCurrentEditingRatePlanChange({
                  surcharge: !surchargeIsChecked,
                });
              }}
              disabled={
                !isEditing || !ratePlan.data?.allowedMethod || rowDisabled
              }
              label=""
              labelProps={{
                className: "mr-0",
              }}
              textProps={{
                className: "max-sm:hidden",
              }}
            />
          </Tooltip>
        </>
      )}
    </div>
  );
}

export default RatePlanRow;
